import { useTimeoutFn } from '@vueuse/core'
import { injectionKey } from '@shared/injection'
import { useConciergeState } from './conciergeState'
import { recommendationReadiness } from './recommendationRequest'

// The contract a concierge input hands the takeover so it can decide when to engage without
// reaching into the input's own DOM. The getters run inside the takeover's reactive computeds,
// so each must read reactive state in the input.
export type ConciergeInputReport = {
  active: () => boolean                // the buyer is actively working in this input right now
  empty: () => boolean                 // the input's whole query is empty
  dismissOnOutsideClick: () => boolean // a click off the input should dismiss it right now
}

export const CONCIERGE_TAKEOVER_KEY
  = injectionKey<ReturnType<typeof setupConciergeTakeover>>('conciergeTakeover')

// Must exceed ConciergeInput's 220ms parse debounce: readyToFetch lags a keystroke by that
// window, so an edit marks the takeover "settling" and holds engagement off the stale-ready
// read until it clears. A timer, not a value-watch — a type-then-backspace writes the same
// string back, which a value-watch would never fire on, so settling would stick.
const SETTLE_CLEAR_MS = 260

function setupConciergeTakeover () {
  const state = useConciergeState()
  const readyToFetch = computed(() => recommendationReadiness(state.signals).ready)

  const report = shallowRef<ConciergeInputReport | null>(null)
  const settling = ref(false)
  const { start: scheduleSettleClear } = useTimeoutFn(
    () => { settling.value = false }, SETTLE_CLEAR_MS, { immediate: false },
  )

  const shouldEngage = computed(() => {
    const input = report.value
    return Boolean(input && readyToFetch.value && input.active() && !input.empty() && !settling.value)
  })

  const dismissOnOutsideClick = computed(() => Boolean(report.value?.dismissOnOutsideClick()))

  function registerInput (input: ConciergeInputReport) {
    report.value = input
    return () => { if (report.value === input) report.value = null }
  }

  // The active input calls this on every keystroke; see SETTLE_CLEAR_MS.
  function markEditing () {
    settling.value = true
    scheduleSettleClear()
  }

  return { shouldEngage, dismissOnOutsideClick, registerInput, markEditing }
}

export function provideConciergeTakeover () {
  const takeover = setupConciergeTakeover()
  provide(CONCIERGE_TAKEOVER_KEY, takeover)
  return takeover
}

// Injected by an input. Returns undefined when rendered without a takeover (Storybook, the
// framed landing placement), so callers guard with `?.` and degrade to a bare input.
export function useConciergeTakeover () {
  return inject(CONCIERGE_TAKEOVER_KEY, undefined)
}
